import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import DialogHeader from "../components/DialogHeader"
import { validateUSPhoneNumber } from "../utils"
import AdditionalOfficesFormSection from "./AdditionalOfficesFormSection"
import IntakeCategorizedDisplay from "./IntakeCategorizedDisplay"
import OfficeAddressFormSection from "./OfficeAddressFormSection"
import RecruitProfileNestedPanel from "./RecruitProfileNestedPanel"
import RecruitProfileSectionCard from "./RecruitProfileSectionCard"
import {
  ACCEPT_NEW_AGENTS_OPTIONS,
  BROKERAGE_TYPE_OPTIONS,
  BROKER_OF_RECORD_DETAIL_FIELD_KEYS,
  CREDIBILITY_STATEMENT_HELPER_TEXT,
  GENDER_OPTIONS,
  INTAKE_BRAND_OPTIONS,
  OBJECTION_RECOMMENDATION_PAIRS,
  PERSONAL_BIO_HIGHLIGHTS_HELPER_TEXT,
  PROFESSIONAL_BIO_HIGHLIGHTS_HELPER_TEXT,
  REQUIRED_INTAKE_FIELD_DEFS,
  STATE_OPTIONS,
  TIMEZONE_OPTIONS,
  WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL,
  YES_NO_OPTIONS,
  acceptNewAgentsLabel,
  buildFormFromApi,
  emptyForm,
  formToApiPayload,
  genderOptionLabel,
  isDifferentBrokerOfRecord,
  normalizeBrandValue,
  normalizeBrokerageTypeValue,
  normalizeStateValue,
  normalizeTimezoneValue,
  validateIntakeForm,
  validateObjectionRecommendationPairs,
  yesNoOptionLabel,
} from "./brokerageProfileIntakeFields"
import { RECRUIT_PROFILE_SECTION_TITLES } from "./recruitProfileSectionStyles"
import { formFieldsStackSx, outlinedInputBorderSx } from "./settingsFormStyles"

export { outlinedInputBorderSx }
export {
  yesNoOptionLabel as yesNoLabel,
  acceptNewAgentsLabel,
  genderOptionLabel,
}

const REQUIRED_FIELD_KEYS = new Set(
  REQUIRED_INTAKE_FIELD_DEFS.filter((d) => !d.fromWebsiteUrl).map((d) => d.key)
)

const CONTACT_FIELDS = [
  { key: "firstName", label: "First Name", lettersOnly: true },
  { key: "firstNamePhonetics", label: "First Name (phonetics)" },
  { key: "lastName", label: "Last Name", lettersOnly: true },
  { key: "lastNamePhonetics", label: "Last Name (phonetics)" },
  { key: "title", label: "Title" },
  { key: "email", label: "Email", type: "email" },
]

const BROKERAGE_FIELDS = [
  { key: "brokerageName", label: "Brokerage Name" },
  { key: "brokerageNamePhonetics", label: "Brokerage Name (phonetics)" },
  { key: "brokeragePhone", label: "Brokerage Phone", phoneUS: true },
]

const BROKER_OF_RECORD_DETAIL_FIELDS = [
  { key: "brokerOfRecord", label: "Broker of Record" },
  {
    key: "brokerOfRecordPhonetics",
    label: "Broker of Record Name Phonetics",
  },
  {
    key: "brokerOfRecordPhone",
    label: "Broker of Record Phone Number",
    phoneUS: true,
  },
  {
    key: "brokerOfRecordEmail",
    label: "Broker of Record Email",
    type: "email",
  },
]

const OFFICE_FIELDS = [
  { key: "numberOfAgents", label: "No. of Agents" },
  { key: "yearsInBusiness", label: "Years in Business" },
]

const FEATURE_FIELDS = [
  {
    key: "keyFeature1",
    label: "Key Feature 1",
    multiline: true,
    minRows: 2,
    helperText: "Example: Supplied leads",
  },
  {
    key: "keyFeature1Short",
    label: "Key Feature 1 Short",
    helperText: "Example: Leads",
  },
  {
    key: "keyFeature2",
    label: "Key Feature 2",
    multiline: true,
    minRows: 2,
    helperText: "Example: 1-on-1 weekly coaching",
  },
  {
    key: "keyFeature2Short",
    label: "Key Feature 2 Short",
    helperText: "Example: Coaching",
  },
  {
    key: "keyFeature3",
    label: "Key Feature 3",
    multiline: true,
    minRows: 2,
    helperText: "Example: 24hr support staff",
  },
  {
    key: "keyFeature3Short",
    label: "Key Feature 3 Short",
    helperText: "Example: 24hr support",
  },
]

const RECRUITING_SELECTS = [
  { key: "acceptPartTimers", label: "Accept Part Timers?" },
  { key: "acceptCommercialAgents", label: "Commercial Agents?" },
  { key: "acceptPropertyManagers", label: "Property Managers?" },
  { key: "acceptLoanOfficers", label: "Mortgage Brokers?" },
]

function RowCentered({ children, sx }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        ...sx,
      }}
    >
      {children}
    </Box>
  )
}

function FormTextField({ spec, form, patchForm, disabled, fieldErrors }) {
  const required = spec.required ?? REQUIRED_FIELD_KEYS.has(spec.key)
  const errorMessage = fieldErrors?.[spec.key]

  function handleChange(e) {
    let next = e.target.value
    if (spec.lettersOnly) {
      next = next.replace(/[0-9]/g, "")
    }
    patchForm({ [spec.key]: next })
  }

  return (
    <TextField
      variant="outlined"
      sx={{ ...outlinedInputBorderSx, width: "100%" }}
      label={spec.label}
      value={form[spec.key]}
      onChange={handleChange}
      size="small"
      fullWidth
      required={required}
      error={Boolean(errorMessage)}
      helperText={errorMessage || spec.helperText || undefined}
      placeholder={spec.placeholder}
      FormHelperTextProps={
        spec.multilineHelper ? { sx: { whiteSpace: "pre-line" } } : undefined
      }
      type={spec.phoneUS || spec.type === "tel" ? "tel" : spec.type || "text"}
      inputProps={spec.lettersOnly ? { inputMode: "text" } : undefined}
      multiline={spec.multiline}
      minRows={spec.minRows}
      disabled={disabled}
    />
  )
}

function FormGenderSelect({
  form,
  patchForm,
  disabled,
  fieldErrors,
  fieldKey = "gender",
  label = "Gender",
  required = true,
}) {
  const hasError = Boolean(fieldErrors?.[fieldKey])
  const labelId = `ai-profile-${fieldKey}`
  return (
    <FormControl
      variant="outlined"
      sx={outlinedInputBorderSx}
      size="small"
      fullWidth
      required={required}
      error={hasError}
      disabled={disabled}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={form[fieldKey]}
        onChange={(e) => patchForm({ [fieldKey]: e.target.value })}
      >
        <MenuItem value="">
          <em>Select…</em>
        </MenuItem>
        {GENDER_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
      <Typography
        variant="caption"
        color={hasError ? "error" : "text.secondary"}
        sx={{ mt: 0.75, ml: 1.75, display: "block" }}
      >
        {fieldErrors?.[fieldKey] || "He/Him, She/Her, or They/Them"}
      </Typography>
    </FormControl>
  )
}

function FormOptionSelect({
  spec,
  form,
  patchForm,
  disabled,
  fieldErrors,
  options,
  normalize,
  emptyHint,
}) {
  const required = REQUIRED_FIELD_KEYS.has(spec.key)
  const hasError = Boolean(fieldErrors?.[spec.key])
  const value = normalize(form[spec.key])
  const optionValues = new Set(options.map((o) => o.value))
  const displayValue = optionValues.has(value) ? value : ""

  return (
    <FormControl
      variant="outlined"
      sx={outlinedInputBorderSx}
      size="small"
      fullWidth
      required={required}
      error={hasError}
      disabled={disabled}
    >
      <InputLabel id={`ai-profile-${spec.key}`}>{spec.label}</InputLabel>
      <Select
        labelId={`ai-profile-${spec.key}`}
        label={spec.label}
        value={displayValue}
        onChange={(e) => patchForm({ [spec.key]: normalize(e.target.value) })}
      >
        <MenuItem value="">
          <em>Select…</em>
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
      {emptyHint ? (
        <Typography
          variant="caption"
          color={hasError ? "error" : "text.secondary"}
          sx={{ mt: 0.75, ml: 1.75, display: "block" }}
        >
          {fieldErrors?.[spec.key] || emptyHint}
        </Typography>
      ) : null}
    </FormControl>
  )
}

function FormYesNoSelect({ spec, form, patchForm, disabled, fieldErrors }) {
  const required = REQUIRED_FIELD_KEYS.has(spec.key)
  const hasError = Boolean(fieldErrors?.[spec.key])
  return (
    <FormControl
      variant="outlined"
      sx={outlinedInputBorderSx}
      size="small"
      fullWidth
      required={required}
      error={hasError}
      disabled={disabled}
    >
      <InputLabel id={`ai-profile-${spec.key}`}>{spec.label}</InputLabel>
      <Select
        labelId={`ai-profile-${spec.key}`}
        label={spec.label}
        value={form[spec.key]}
        onChange={(e) => patchForm({ [spec.key]: e.target.value })}
      >
        <MenuItem value="">
          <em>Select…</em>
        </MenuItem>
        {YES_NO_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

function renderTextFields(specs, form, patchForm, disabled, fieldErrors) {
  return (
    <Column sx={formFieldsStackSx}>
      {specs.map((spec) => (
        <FormTextField
          key={spec.key}
          spec={spec}
          form={form}
          patchForm={patchForm}
          disabled={disabled}
          fieldErrors={fieldErrors}
        />
      ))}
    </Column>
  )
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.websiteUrl
 * @param {object | null} props.initialFromApi
 * @param {boolean} props.isLoadingApi
 * @param {string | null} props.apiError
 * @param {boolean} [props.isSubmitting]
 * @param {(payload: object) => void | Promise<void>} props.onSave
 * @param {(payload: object) => void | Promise<void>} props.onSubmit
 */
export default function AiProfileGenerationDialog({
  open,
  onClose,
  websiteUrl,
  initialFromApi,
  isLoadingApi,
  apiError,
  isSubmitting = false,
  onSave,
  onSubmit,
}) {
  const [form, setForm] = useState(emptyForm)
  const [fieldErrors, setFieldErrors] = useState({})
  const [submitError, setSubmitError] = useState(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false)
  const [isSavingLocal, setIsSavingLocal] = useState(false)
  const [detailsConfirmed, setDetailsConfirmed] = useState(false)

  useEffect(() => {
    if (!open) {
      setPreviewOpen(false)
      setDetailsConfirmed(false)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      return
    }
    setForm(emptyForm())
    setFieldErrors({})
    setSubmitError(null)
    setDetailsConfirmed(false)
  }, [open])

  useEffect(() => {
    if (!open || !initialFromApi) {
      return
    }
    if (isLoadingApi) {
      return
    }
    setForm(buildFormFromApi(initialFromApi))
  }, [open, isLoadingApi, initialFromApi])

  function patchForm(updates) {
    setForm((prev) => ({ ...prev, ...updates }))
    setDetailsConfirmed(false)
    setFieldErrors((prev) => {
      const next = { ...prev }
      Object.keys(updates).forEach((k) => {
        if (
          k === "mobileNumber" ||
          k === "brokeragePhone" ||
          k === "brokerOfRecordPhone"
        ) {
          const check = validateUSPhoneNumber(updates[k], {
            required: k !== "brokerOfRecordPhone",
          })
          if (!check.valid) {
            next[k] = check.message
          } else {
            delete next[k]
          }
        } else {
          delete next[k]
        }
      })
      return next
    })
    if (submitError) {
      setSubmitError(null)
    }
  }

  function runValidation() {
    const result = validateIntakeForm(form, websiteUrl)
    setFieldErrors(result.fieldErrors)
    setSubmitError(result.valid ? null : result.message)
    return result.valid
  }

  function handlePreviewClick() {
    if (!runValidation()) {
      return
    }
    setPreviewOpen(true)
  }

  function runPairValidation() {
    const result = validateObjectionRecommendationPairs(form)
    if (result.valid) {
      return true
    }
    setFieldErrors((prev) => ({ ...prev, ...result.fieldErrors }))
    setSubmitError(result.message)
    return false
  }

  async function handleSave() {
    if (!runPairValidation()) {
      return
    }
    setIsSavingLocal(true)
    try {
      await onSave(
        formToApiPayload(form, websiteUrl, { syncToAirtable: false })
      )
      onClose()
    } catch {
      // Parent shows error; keep dialog open for corrections.
    } finally {
      setIsSavingLocal(false)
    }
  }

  async function handleSubmit() {
    if (!detailsConfirmed) {
      return
    }
    if (!runValidation()) {
      return
    }
    setIsSubmittingLocal(true)
    try {
      await onSubmit(
        formToApiPayload(form, websiteUrl, { syncToAirtable: true })
      )
      onClose()
    } catch {
      // Parent shows error; keep dialog open for corrections.
    } finally {
      setIsSubmittingLocal(false)
    }
  }

  const submitInProgress = isSubmitting || isSubmittingLocal
  const saveInProgress = isSavingLocal
  const persistInProgress = submitInProgress || saveInProgress

  function handleDialogClose() {
    onClose()
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        disableEscapeKeyDown={isLoadingApi}
      >
        <DialogHeader
          title="Profile Details for Recruiting Campaign"
          onClose={handleDialogClose}
          showCloseButton={!isLoadingApi}
        />
        <DialogContent
          sx={{
            pt: 2,
            pb: 2,
            px: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "background.default"
                : theme.palette.grey[50],
          }}
        >
          <Column sx={{ gap: 2, alignItems: "stretch", maxWidth: "100%" }}>
            {isLoadingApi ? (
              <RecruitProfileSectionCard title="Loading" useNestedPanel>
                <RowCentered sx={{ gap: 2, py: 1 }}>
                  <CircularProgress size={28} />
                  <Typography color="text.secondary">
                    Preparing profile form…
                  </Typography>
                </RowCentered>
              </RecruitProfileSectionCard>
            ) : null}

            {apiError ? (
              <Alert severity="error" variant="outlined">
                {apiError} You can still complete the form manually.
              </Alert>
            ) : null}

            <RecruitProfileSectionCard
              title="Website source"
              subheader={`${WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL} for this profile.`}
            >
              <TextField
                variant="outlined"
                sx={outlinedInputBorderSx}
                label={WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL}
                value={websiteUrl}
                size="small"
                fullWidth
                disabled
                InputProps={{ readOnly: true }}
              />
            </RecruitProfileSectionCard>

            <RecruitProfileSectionCard
              title="Contact"
              subheader="Primary contact details."
            >
              <Column sx={formFieldsStackSx}>
                {renderTextFields(
                  CONTACT_FIELDS,
                  form,
                  patchForm,
                  isLoadingApi,
                  fieldErrors
                )}
                <FormTextField
                  spec={{
                    key: "mobileNumber",
                    label: "Mobile Phone Number",
                    phoneUS: true,
                  }}
                  form={form}
                  patchForm={patchForm}
                  disabled={isLoadingApi}
                  fieldErrors={fieldErrors}
                />
                <FormGenderSelect
                  form={form}
                  patchForm={patchForm}
                  disabled={isLoadingApi}
                  fieldErrors={fieldErrors}
                />
              </Column>
            </RecruitProfileSectionCard>

            <RecruitProfileSectionCard
              title="Brokerage"
              subheader="Brokerage identity and phone."
            >
              <Column sx={formFieldsStackSx}>
                {renderTextFields(
                  BROKERAGE_FIELDS,
                  form,
                  patchForm,
                  isLoadingApi,
                  fieldErrors
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isDifferentBrokerOfRecord(form)}
                      disabled={isLoadingApi}
                      onChange={(e) => {
                        const checked = e.target.checked
                        if (checked) {
                          patchForm({ differentBrokerOfRecord: true })
                          return
                        }
                        const cleared = { differentBrokerOfRecord: false }
                        for (const key of BROKER_OF_RECORD_DETAIL_FIELD_KEYS) {
                          cleared[key] = ""
                        }
                        patchForm(cleared)
                        setFieldErrors((prev) => {
                          const next = { ...prev }
                          for (const key of BROKER_OF_RECORD_DETAIL_FIELD_KEYS) {
                            delete next[key]
                          }
                          return next
                        })
                      }}
                    />
                  }
                  label="Different Broker of Record?"
                  sx={{ mx: 0 }}
                />
                {isDifferentBrokerOfRecord(form) ? (
                  <Column sx={formFieldsStackSx}>
                    {renderTextFields(
                      BROKER_OF_RECORD_DETAIL_FIELDS,
                      form,
                      patchForm,
                      isLoadingApi,
                      fieldErrors
                    )}
                    <FormGenderSelect
                      form={form}
                      patchForm={patchForm}
                      disabled={isLoadingApi}
                      fieldErrors={fieldErrors}
                      fieldKey="brokerOfRecordGender"
                      label="Broker of Record Gender"
                      required={false}
                    />
                  </Column>
                ) : null}
                <FormOptionSelect
                  spec={{ key: "brokerageType", label: "Brokerage Type" }}
                  form={form}
                  patchForm={patchForm}
                  disabled={isLoadingApi}
                  fieldErrors={fieldErrors}
                  options={BROKERAGE_TYPE_OPTIONS}
                  normalize={normalizeBrokerageTypeValue}
                  emptyHint="Brick and Mortar, Virtual, or Hybrid"
                />
                <FormOptionSelect
                  spec={{ key: "brand", label: "Brand" }}
                  form={form}
                  patchForm={patchForm}
                  disabled={isLoadingApi}
                  fieldErrors={fieldErrors}
                  options={INTAKE_BRAND_OPTIONS}
                  normalize={normalizeBrandValue}
                  emptyHint="Select your franchise brand"
                />
              </Column>
            </RecruitProfileSectionCard>

            <RecruitProfileSectionCard
              title={RECRUIT_PROFILE_SECTION_TITLES.officeAddress}
              subheader="Primary office address and landmark."
            >
              <OfficeAddressFormSection
                form={form}
                patchForm={patchForm}
                disabled={isLoadingApi}
                fieldErrors={fieldErrors}
                renderTextFields={renderTextFields}
                stateSelect={
                  <FormOptionSelect
                    spec={{ key: "state", label: "State" }}
                    form={form}
                    patchForm={patchForm}
                    disabled={isLoadingApi}
                    fieldErrors={fieldErrors}
                    options={STATE_OPTIONS}
                    normalize={normalizeStateValue}
                    emptyHint="US state or Canadian province/territory code"
                  />
                }
                timezoneSelect={
                  <FormOptionSelect
                    spec={{ key: "timezone", label: "Time Zone" }}
                    form={form}
                    patchForm={patchForm}
                    disabled={isLoadingApi}
                    fieldErrors={fieldErrors}
                    options={TIMEZONE_OPTIONS}
                    normalize={normalizeTimezoneValue}
                    emptyHint="Select your brokerage time zone"
                  />
                }
                numberOfOfficesField={
                  <FormTextField
                    spec={{ key: "numberOfOffices", label: "No. of Offices" }}
                    form={form}
                    patchForm={patchForm}
                    disabled={isLoadingApi}
                    fieldErrors={fieldErrors}
                  />
                }
              />
            </RecruitProfileSectionCard>

            <RecruitProfileSectionCard
              title={RECRUIT_PROFILE_SECTION_TITLES.additionalOffices}
              subheader="Optional extra office locations beyond the primary address."
            >
              <AdditionalOfficesFormSection
                form={form}
                patchForm={patchForm}
                setFieldErrors={setFieldErrors}
                disabled={isLoadingApi}
                fieldErrors={fieldErrors}
                renderTextFields={renderTextFields}
              />
            </RecruitProfileSectionCard>

            <RecruitProfileSectionCard
              title={RECRUIT_PROFILE_SECTION_TITLES.officesScale}
              subheader="Scale and trust signals."
              useNestedPanel={false}
            >
              <RecruitProfileNestedPanel title="Scale">
                {renderTextFields(
                  OFFICE_FIELDS,
                  form,
                  patchForm,
                  isLoadingApi,
                  fieldErrors
                )}
              </RecruitProfileNestedPanel>
              <RecruitProfileNestedPanel title="Credibility">
                <FormTextField
                  spec={{
                    key: "credibilityStatement",
                    label: "Credibility Statement",
                    multiline: true,
                    minRows: 3,
                    helperText: CREDIBILITY_STATEMENT_HELPER_TEXT,
                    multilineHelper: true,
                  }}
                  form={form}
                  patchForm={patchForm}
                  disabled={isLoadingApi}
                  fieldErrors={fieldErrors}
                />
              </RecruitProfileNestedPanel>
            </RecruitProfileSectionCard>

            <RecruitProfileSectionCard
              title="Key features"
              subheader="Up to three differentiators (full and short labels)."
              useNestedPanel={false}
            >
              <RecruitProfileNestedPanel title="Feature 1">
                {renderTextFields(
                  FEATURE_FIELDS.slice(0, 2),
                  form,
                  patchForm,
                  isLoadingApi,
                  fieldErrors
                )}
              </RecruitProfileNestedPanel>
              <RecruitProfileNestedPanel title="Feature 2">
                {renderTextFields(
                  FEATURE_FIELDS.slice(2, 4),
                  form,
                  patchForm,
                  isLoadingApi,
                  fieldErrors
                )}
              </RecruitProfileNestedPanel>
              <RecruitProfileNestedPanel title="Feature 3">
                {renderTextFields(
                  FEATURE_FIELDS.slice(4, 6),
                  form,
                  patchForm,
                  isLoadingApi,
                  fieldErrors
                )}
              </RecruitProfileNestedPanel>
            </RecruitProfileSectionCard>

            <RecruitProfileSectionCard
              title="Bio highlights"
              subheader="Optional personal details for Signature Campaigns — beyond typical real estate credentials."
              useNestedPanel={false}
            >
              <RecruitProfileNestedPanel title="Professional">
                <FormTextField
                  spec={{
                    key: "professionalBioHighlights",
                    label: "Professional Bio Highlights",
                    multiline: true,
                    minRows: 3,
                    helperText: PROFESSIONAL_BIO_HIGHLIGHTS_HELPER_TEXT,
                    multilineHelper: true,
                  }}
                  form={form}
                  patchForm={patchForm}
                  disabled={isLoadingApi}
                  fieldErrors={fieldErrors}
                />
              </RecruitProfileNestedPanel>
              <RecruitProfileNestedPanel title="Personal">
                <FormTextField
                  spec={{
                    key: "personalBioHighlights",
                    label: "Personal Bio Highlights",
                    multiline: true,
                    minRows: 3,
                    helperText: PERSONAL_BIO_HIGHLIGHTS_HELPER_TEXT,
                    multilineHelper: true,
                  }}
                  form={form}
                  patchForm={patchForm}
                  disabled={isLoadingApi}
                  fieldErrors={fieldErrors}
                />
              </RecruitProfileNestedPanel>
            </RecruitProfileSectionCard>

            <RecruitProfileSectionCard
              title={RECRUIT_PROFILE_SECTION_TITLES.commonObjections}
              subheader="Common objections agents raise when considering a move."
              useNestedPanel={false}
            >
              {OBJECTION_RECOMMENDATION_PAIRS.map((pair) => (
                <RecruitProfileNestedPanel
                  key={pair.objectionKey}
                  title={pair.objectionLabel}
                >
                  {renderTextFields(
                    [
                      {
                        key: pair.objectionKey,
                        label: pair.objectionLabel,
                        placeholder: "Enter objection",
                        multiline: true,
                        minRows: 3,
                      },
                      {
                        key: pair.recommendationKey,
                        label: pair.recommendationLabel,
                        placeholder: "Enter the recommended response here",
                        multiline: true,
                        minRows: 3,
                        required:
                          String(form[pair.objectionKey] ?? "").trim() !== "",
                      },
                    ],
                    form,
                    patchForm,
                    isLoadingApi,
                    fieldErrors
                  )}
                </RecruitProfileNestedPanel>
              ))}
            </RecruitProfileSectionCard>

            <RecruitProfileSectionCard
              title="Recruiting acceptance"
              subheader="Who the brokerage accepts."
            >
              <Column sx={formFieldsStackSx}>
                {RECRUITING_SELECTS.map((spec) => (
                  <FormYesNoSelect
                    key={spec.key}
                    spec={spec}
                    form={form}
                    patchForm={patchForm}
                    disabled={isLoadingApi}
                    fieldErrors={fieldErrors}
                  />
                ))}
                <FormControl
                  variant="outlined"
                  sx={outlinedInputBorderSx}
                  size="small"
                  fullWidth
                  disabled={isLoadingApi}
                >
                  <InputLabel id="ai-profile-acceptNewAgents">
                    Accept New Agents?
                  </InputLabel>
                  <Select
                    labelId="ai-profile-acceptNewAgents"
                    label="Accept New Agents?"
                    value={form.acceptNewAgents}
                    onChange={(e) =>
                      patchForm({ acceptNewAgents: e.target.value })
                    }
                  >
                    <MenuItem value="">
                      <em>Select…</em>
                    </MenuItem>
                    {ACCEPT_NEW_AGENTS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
            </RecruitProfileSectionCard>

            {submitError ? (
              <Alert severity="warning" sx={{ whiteSpace: "pre-wrap" }}>
                {submitError}
              </Alert>
            ) : null}

            <FormControlLabel
              control={
                <Checkbox
                  checked={detailsConfirmed}
                  onChange={(e) => setDetailsConfirmed(e.target.checked)}
                  disabled={isLoadingApi || persistInProgress}
                />
              }
              label="I confirm the details above are accurate."
              sx={{
                alignItems: "flex-start",
                mx: 0,
                "& .MuiCheckbox-root": { pt: 0.25 },
              }}
            />
          </Column>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            pt: 1.5,
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "space-between",
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Button variant="outlined" onClick={handleDialogClose}>
            Cancel
          </Button>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handlePreviewClick}
              disabled={isLoadingApi || persistInProgress}
            >
              Preview
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSave}
              disabled={isLoadingApi || persistInProgress}
            >
              {saveInProgress ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoadingApi || persistInProgress || !detailsConfirmed}
            >
              {submitInProgress ? "Submitting…" : "Submit"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogHeader title="Preview" onClose={() => setPreviewOpen(false)} />
        <DialogContent
          sx={{
            pt: 2,
            pb: 2,
            px: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "background.default"
                : theme.palette.grey[50],
          }}
        >
          <IntakeCategorizedDisplay intake={form} websiteUrl={websiteUrl} />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            pt: 1,
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <Button variant="outlined" onClick={() => setPreviewOpen(false)}>
            Back to edit
          </Button>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setPreviewOpen(false)
                handleSave()
              }}
              disabled={persistInProgress}
            >
              {saveInProgress ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setPreviewOpen(false)
                handleSubmit()
              }}
              disabled={persistInProgress || !detailsConfirmed}
            >
              {submitInProgress ? "Submitting…" : "Submit"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}

export function parseCityStateFromAddress(address) {
  const raw = (address || "").trim()
  if (!raw) {
    return { city: "", state: "" }
  }
  const parts = raw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length >= 2) {
    const last = parts[parts.length - 1]
    if (last.length === 2 && /^[A-Za-z]{2}$/.test(last)) {
      return {
        city: parts.slice(0, -1).join(", "),
        state: last.toUpperCase(),
      }
    }
  }
  return { city: raw, state: "" }
}
